/**
 * Team settings: one overlay sheet over the Team queue, with anchored
 * sections (the prototype's `#settings`). It holds everything the Team edit
 * sheet, the line pages and the Playbook sheet held: the Team itself, then one
 * card per Agent on it, in the order work moves (Bailey, Reeve, Wright,
 * Proctor, Fletcher, Harper).
 *
 * It opens from the gear on the queue header (at the top) and from every
 * rhythm chip (at the Agent that owns it), and its address is `?settings=`:
 * a section, or a line's id from the retired line pages. The rail folds to
 * its strip while it is open, so the queue keeps a column.
 *
 * Edits are drafts until Save, and Save writes only what changed, through the
 * Team's existing routes (`settings/settingsDraft.ts`). The acts that are not
 * settings (Pause, Archive, Turn off, Remove, Propose now, a Playbook rule, a
 * new custom post) land at once and say so. Escape and the scrim close it.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Job } from "@yagni/shared";

import { Btn, CloseIcon, cx } from "../queue/index.js";
import {
  useAuthSession,
  useCan,
  useCanManageTeam,
  useIsWorkspaceAdmin,
} from "../hooks/useWorkspaceRole.js";
import { useFoldRail } from "../shell/railFold.js";
import { SUBSCRIPTIONS_CHANGED_EVENT } from "../shell/useNavDesks.js";
import { useAvailableRepos } from "../workers/useWorkers.js";
import { captureUiError } from "../sentry.js";
import { listSystems } from "../settings/systemsApi.js";
import type { CheckDrafts } from "./ChecksRoster.js";
import { refusalLine } from "./copy.js";
import { askRowsFrom, sheetAudience } from "./teamEdit.js";
import { TeamWriteError } from "./teamsApi.js";
import { attachedRepos, type TeamPageState } from "./useTeamPage.js";
import {
  AGENT_DEFAULTS,
  BaileySection,
  FletcherSection,
  ProctorSection,
  ReeveSection,
  WrightSection,
} from "./settings/AgentSections.js";
import type { Person, SettingsCtx } from "./settings/context.js";
import { Mark } from "./settings/fields.js";
import { HarperSection } from "./settings/HarperSection.js";
import { runLimitsDraftOf } from "./settings/runLimits.js";
import { RunLimitsSection } from "./settings/RunLimitsSection.js";
import {
  CRAFT_OF_SECTION,
  SETTINGS_SECTIONS,
  editsBesideFilter,
  initialFilterText,
  lineDraftOf,
  readPath,
  readsAttachment,
  saveTeamSettings,
  teamLadderOfPage,
  whoseValue,
  type LineDraft,
  type SettingsSection,
  type TeamDraft,
} from "./settings/settingsDraft.js";
import { TeamSection } from "./settings/TeamSection.js";
import "./settings/settings.css";

export interface TeamSettingsSheetProps {
  deskId: string;
  page: TeamPageState;
  open: boolean;
  /** The section to open at. */
  at: SettingsSection;
  /** A field inside the section to scroll to (`people`). */
  anchor?: string;
  onClose: () => void;
}

export function TeamSettingsSheet(props: TeamSettingsSheetProps) {
  useFoldRail(props.open);
  if (!props.open || !props.page.team) return null;
  return <SettingsOverlay {...props} />;
}

function teamDraftFrom(page: TeamPageState): TeamDraft {
  const att = readsAttachment(page.attachments);
  const ladder = teamLadderOfPage(page.team, page.jobs);
  return {
    name: page.team?.name ?? "",
    owns: page.team?.mission ?? "",
    repos: attachedRepos(page.attachments),
    filter: initialFilterText(page.attachments),
    tracker: att?.source === "linear" ? "linear" : "jira",
    whose: whoseValue(page.ticketsAssignedTo),
    channel: page.channels[0]?.channelId ?? "",
    people: page.members.map((member) => member.userId),
    asks: askRowsFrom(page.answerers),
    audience: sheetAudience(page.team?.visibility ?? null),
    uiPreview: page.team?.uiPreviewEnabled ?? true,
    autonomy: ladder.override ? null : ladder.step,
    limits: runLimitsDraftOf(page.team?.ticketBudget, page.team?.dailyTurnBudget),
    declaredSystemId: page.team?.declaredSystemId ?? null,
  };
}

function usePeople(page: TeamPageState): Person[] {
  const [workspace, setWorkspace] = useState<Person[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/members", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`members read failed (${res.status})`);
        return res.json();
      })
      .then((body: unknown) => {
        const list = (body as { members?: unknown } | null)?.members;
        if (cancelled || !Array.isArray(list)) return;
        setWorkspace(
          list
            .filter(
              (
                entry,
              ): entry is {
                userId: string;
                displayName?: string;
                email?: string;
                avatarUrl?: string | null;
              } => typeof entry?.userId === "string",
            )
            .map((entry) => ({
              userId: entry.userId,
              name: entry.displayName?.trim() || entry.email || entry.userId,
              email: entry.email ?? null,
              avatarUrl: entry.avatarUrl ?? null,
            })),
        );
      })
      .catch((err: unknown) => {
        // The pickers fall back to the Team's own members.
        captureUiError(err, {
          area: "Teams",
          operation: "settings_read_members",
          severity: "warning",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return useMemo(() => {
    const seen = new Set<string>();
    const out: Person[] = [];
    for (const person of workspace) {
      if (seen.has(person.userId)) continue;
      seen.add(person.userId);
      out.push(person);
    }
    for (const member of page.members) {
      if (seen.has(member.userId)) continue;
      seen.add(member.userId);
      out.push({
        userId: member.userId,
        name: member.displayName?.trim() || member.email || member.userId,
        email: member.email,
        avatarUrl: member.avatarUrl ?? null,
      });
    }
    return out;
  }, [workspace, page.members]);
}

function useSlackChannels(): Array<{ id: string; name: string }> {
  const [channels, setChannels] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/slack/channels", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`slack channels read failed (${res.status})`);
        return res.json();
      })
      .then((body: unknown) => {
        const list = (body as { channels?: unknown } | null)?.channels;
        if (cancelled || !Array.isArray(list)) return;
        setChannels(
          list.filter(
            (entry): entry is { id: string; name: string } =>
              typeof entry?.id === "string" && typeof entry?.name === "string",
          ),
        );
      })
      .catch((err: unknown) => {
        captureUiError(err, {
          area: "Teams",
          operation: "settings_read_slack_channels",
          severity: "warning",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return channels;
}

/**
 * The workspace's declared systems, read once; empty when the read is refused.
 * A viewer without `manage-repos` is never read for them (the server refuses
 * it): the select then offers None and the Team's own pick, by name.
 */
function useSystems(readable: boolean): Array<{ id: string; name: string }> {
  const [systems, setSystems] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    if (!readable) return;
    let cancelled = false;
    listSystems()
      .then((list) => {
        if (!cancelled) setSystems(list.map((system) => ({ id: system.id, name: system.name })));
      })
      .catch((err: unknown) => {
        // The select then offers None and the Team's own pick.
        captureUiError(err, {
          area: "Teams",
          operation: "settings_read_systems",
          severity: "warning",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [readable]);
  return systems;
}

/** Why a Save cannot go yet, from the drafts alone; null when it can. */
function blockedBy(page: TeamPageState, lines: Record<string, LineDraft>): string | null {
  for (const job of page.jobs) {
    const draft = lines[job.id];
    if (!draft || job.kind !== "chronicler.custom") continue;
    const category = readPath(draft.body, "category");
    const instructions = String(readPath(draft.body, "instructions") ?? "").trim();
    if (category === "other" && !instructions)
      return `Say what ${job.instance} reports on before saving.`;
  }
  return null;
}

function SettingsOverlay({ deskId, page, at, anchor, onClose }: TeamSettingsSheetProps) {
  const session = useAuthSession();
  // The one Team rule: the Team's owner or a workspace owner or admin changes
  // every field, its name included, whoever created the Team. The operator
  // in God Mode with Act on reads as the workspace's owner (`useWorkspaceRole`).
  const canManage = useCanManageTeam(page.team);
  const isAdmin = useIsWorkspaceAdmin(true);
  const readOnly = !canManage || page.team?.archivedAt != null;
  const people = usePeople(page);
  const slackChannels = useSlackChannels();
  const { names: availableRepos, scope: repoScope } = useAvailableRepos(!readOnly);
  // A standalone mount (no session) reads as allowed, as Settings shows Systems.
  const systems = useSystems(useCan("manage-repos", true));

  const initial = useMemo(() => teamDraftFrom(page), []); // eslint-disable-line react-hooks/exhaustive-deps -- the draft starts from what the sheet opened on
  const [team, setTeamState] = useState<TeamDraft>(initial);
  const [lines, setLines] = useState<Record<string, LineDraft>>({});
  const [checkDrafts, setCheckDrafts] = useState<CheckDrafts>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; tone: "ok" | "err" } | null>(null);
  const [matches, setMatches] = useState<number | null>(null);
  const [active, setActive] = useState<SettingsSection>(at);
  const [shown, setShown] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const goto = useCallback((section: string) => {
    const target = bodyRef.current?.querySelector<HTMLElement>(`#set-${section}`);
    target?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    if ((SETTINGS_SECTIONS as readonly string[]).includes(section))
      setActive(section as SettingsSection);
  }, []);

  useEffect(() => {
    setActive(at);
    const target = anchor ?? (at === "team" ? null : at);
    if (!target) return;
    const frame = requestAnimationFrame(() => {
      bodyRef.current
        ?.querySelector<HTMLElement>(`#set-${target}`)
        ?.scrollIntoView?.({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [at, anchor]);

  // Escape closes the sheet. On the document, so a control that keeps Escape
  // for itself (an open chip input) stops it first, and before the shell's
  // own Escape on the window, which this marks as used.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const line = useCallback((job: Job) => lines[job.id] ?? lineDraftOf(job), [lines]);
  const setLine = useCallback((job: Job, update: (draft: LineDraft) => LineDraft) => {
    setLines((prev) => ({ ...prev, [job.id]: update(prev[job.id] ?? lineDraftOf(job)) }));
  }, []);
  const setTeam = useCallback(
    (update: (draft: TeamDraft) => TeamDraft) => setTeamState((prev) => update(prev)),
    [],
  );

  const act = useCallback(
    async (run: () => Promise<unknown>, done?: string) => {
      setBusy(true);
      setMessage(null);
      try {
        await run();
        await page.refetch();
        if (done) setMessage({ text: done, tone: "ok" });
        return true;
      } catch (err) {
        setMessage({ text: refusalText(err), tone: "err" });
        return false;
      } finally {
        setBusy(false);
      }
    },
    [page],
  );

  const agentFor = useCallback(
    (craft: string) => page.agents.find((agent) => agent.craft === craft) ?? null,
    [page.agents],
  );
  const kindOf = useCallback((kind: string) => page.kindsById.get(kind) ?? null, [page.kindsById]);

  const ctx: SettingsCtx = {
    deskId,
    page,
    readOnly,
    busy,
    isAdmin,
    viewerId: session?.user?.id ?? null,
    team,
    setTeam,
    line,
    setLine,
    checkDrafts,
    setCheckDrafts,
    people,
    slackChannels,
    availableRepos,
    repoScope,
    systems,
    act,
    goto,
    matches,
    setMatches,
    editsBesideFilter: editsBesideFilter(initial, team, page.jobs, lines, checkDrafts),
    agentFor,
    kindOf,
  };

  const save = async () => {
    const blocked = blockedBy(page, lines);
    if (blocked) {
      setMessage({ text: blocked, tone: "err" });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const outcome = await saveTeamSettings({
        deskId,
        initial,
        next: team,
        attachments: page.attachments,
        members: page.members,
        jobs: page.jobs,
        lines,
        checkDrafts,
      });
      if (outcome.renamed) window.dispatchEvent(new Event(SUBSCRIPTIONS_CHANGED_EVENT));
      if (outcome.wrote) await page.refetch();
      if (outcome.notes.length > 0) {
        setMessage({ text: outcome.notes.join(" "), tone: "ok" });
        return;
      }
      setMessage({
        text: outcome.wrote ? "Saved. It applies to the next run." : "Nothing changed.",
        tone: "ok",
      });
      window.setTimeout(onClose, outcome.wrote ? 500 : 0);
    } catch (err) {
      // A refusal a person caused (a 4xx) is theirs to read; anything else is ours.
      if (!(err instanceof TeamWriteError) || err.status >= 500) {
        captureUiError(err, { area: "Teams", operation: "settings_save", deskId });
      }
      setMessage({ text: refusalText(err), tone: "err" });
      await page.refetch();
    } finally {
      setBusy(false);
    }
  };

  const nav: Array<{ id: SettingsSection; label: string; mark: ReactNode }> = SETTINGS_SECTIONS.map(
    (id) => {
      if (id === "team")
        return { id, label: team.name || page.team?.name || "The Team", mark: null };
      if (id === "limits") return { id, label: "Run limits", mark: null };
      const agent = agentFor(CRAFT_OF_SECTION[id]);
      const name = agent?.name ?? AGENT_DEFAULTS[id].name;
      return {
        id,
        label: name,
        mark: agent ? (
          <Mark avatar={agent.avatar} name={name} slug={agent.personaSlug} size={16} />
        ) : null,
      };
    },
  );

  const title = page.team?.name ?? team.name;

  // On the body: the queue column clips and contains fixed children (its glass
  // blur makes a containing block), and the overlay covers the whole window.
  return createPortal(
    <div className="teamset-root">
      <div
        className={cx("scrim", shown && "on")}
        onClick={onClose}
        data-testid="settings-scrim"
        aria-hidden
      />
      <aside
        className={cx("overlay wide teamset", shown && "on")}
        role="dialog"
        aria-modal="true"
        aria-label={`${title} · settings`}
        data-testid="team-settings"
      >
        <div className="ohd">
          <h2>{title} · settings</h2>
          <Btn icon aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </Btn>
        </div>
        <nav className="setnav" aria-label="Sections">
          {nav.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={cx(active === entry.id && "on")}
              aria-current={active === entry.id ? "true" : undefined}
              data-goto={entry.id}
              onClick={() => goto(entry.id)}
            >
              {entry.mark}
              {entry.label}
            </button>
          ))}
        </nav>
        <div className="obody" ref={bodyRef}>
          <p className="body-p">
            Everything the Team runs on, in one place. Where a person still signs is the lever that
            matters first; the rest is here when you want it.
            {readOnly &&
              " You can read these settings; the Team's owner or a workspace admin changes them."}
          </p>
          <TeamSection ctx={ctx} />
          <BaileySection ctx={ctx} />
          <ReeveSection ctx={ctx} />
          <WrightSection ctx={ctx} />
          <ProctorSection ctx={ctx} />
          <FletcherSection ctx={ctx} />
          <HarperSection ctx={ctx} />
          <RunLimitsSection ctx={ctx} />
        </div>
        <div className="sfoot">
          <span className="why" role="status" data-testid="settings-message">
            {message ? (
              <span
                style={{
                  color: message.tone === "err" ? "var(--incident)" : "var(--accent-active)",
                }}
              >
                {message.text}
              </span>
            ) : (
              <>
                Changes apply to the next run or review. <b>Work already waiting on you</b> starts
                on its own once its step runs without you.
              </>
            )}
          </span>
          <span className="verbs">
            <Btn variant="quiet" onClick={onClose} disabled={busy}>
              Cancel
            </Btn>
            {!readOnly && (
              <Btn
                variant="primary"
                onClick={() => void save()}
                disabled={busy || !team.name.trim()}
                data-testid="settings-save"
              >
                {busy ? "Saving" : "Save"}
              </Btn>
            )}
          </span>
        </div>
      </aside>
    </div>,
    document.body,
  );
}

function refusalText(err: unknown): string {
  if (err instanceof TeamWriteError) return refusalLine(err.code, err.message);
  if (err instanceof Error) return err.message;
  return "Could not reach the server. Try again.";
}
